import { ApplicationState } from 'framework/applicationState';
import { getSharedDictionary } from 'core/localization/utils/getSharedDictionary';

export type TranslationDictionary = Record<string, Record<string, string>>;
type LocalizationGetOptions = {
	capitalize?: boolean;
};

function isLocalization(d: Record<string, Record<string, string>> | Localization): d is Localization {
	return d.dictionary != null;
}

export class Localization {
	dictionary: TranslationDictionary;

	constructor(...translations: (Record<string, Record<string, string>> | Localization)[]) {
		this.dictionary = {};
		for (const translationsSet of translations) {
			const dictionary = isLocalization(translationsSet) ? translationsSet.dictionary : translationsSet;

			Object.assign(this.dictionary, dictionary);
		}
	}

	get(key: string, ...args: unknown[]): string {
		const sharedDictionary = getSharedDictionary();

		let options: LocalizationGetOptions = {};
		if (args.length > 0 && typeof args[0] === 'object') {
			options = args[0];
			args.splice(0, 1);
		}

		const lowerCaseKey = key.toLowerCase();
		const keyCapitalized = lowerCaseKey != key;

		options = Object.assign(
			{
				capitalize: keyCapitalized,
			},
			options,
		);

		let value = this.tryDictionary(this.dictionary, key);

		if (value == null) {
			value = this.tryDictionary(this.dictionary, lowerCaseKey);
		}

		if (value == null) {
			value = this.tryDictionary(sharedDictionary, key);
		}

		if (value == null) {
			value = this.tryDictionary(sharedDictionary, lowerCaseKey);
		}

		if (value != null) {
			if (options.capitalize) {
				value = value.charAt(0).toUpperCase() + value.slice(1);
			}
		}

		if (value == null) {
			value = key;
			console.error('No localization found for ' + key);
		}

		if (args.length > 0) {
			value = this.formatString(value, args as string[]);
		}

		return value;
	}

	tryDictionary(dictionary: TranslationDictionary, key: string) {
		if (dictionary[key] == null) return null;

		const entry = dictionary[key];
		if (entry[this.getLanguageKey()] != null) return entry[this.getLanguageKey()];

		if (this.getLanguageKey() == 'en') {
			return key;
		}

		return null;
	}

	getLanguageKey() {
		return ApplicationState.locale == 'no' ? 'no' : 'en';
	}

	formatString(value: string, replacements: string[]) {
		return value.replace(/{(\d+)}/g, function (match, number: number) {
			return typeof replacements[number] != 'undefined' ? replacements[number] : match;
		});
	}

	getTranslator() {
		return this.get.bind(this);
	}
}

export const translator = function (...args: TranslationDictionary[]) {
	const localization = new Localization(...args);
	return localization.getTranslator();
};

export default translator;

export const i = translator();

export function getNumericTranslation(count: number, singularForm: string, pluralForm: string) {
	return count == 1 ? singularForm : pluralForm;
}

export function getNumericTranslationStart(count: number, singularForm: string, pluralForm: string) {
	return count + ' ' + getNumericTranslation(count, singularForm, pluralForm);
}
